import _ from 'underscore';
import { Scheduler } from '@reactivex/rxjs/dist/cjs/Scheduler';
import shallowEqual from 'react-recompose/shallowEqual';
import { decodeGpuIndex } from '../support/picking';
import { $ref } from '@graphistry/falcor-json-graph';
import {
    isAnimating,
    hitmapUpdates,
    labelSettings,
    cameraChanges,
    curPoints
} from '@graphistry/viz/src/client/legacy';


const TIME_BETWEEN_SAMPLES = 300; // ms

export function pickPointsOfInterest(action$) {
    return labelSettings
        .combineLatest(cameraChanges, (props, camera) => ({ ...props, zoom: camera.zoom }))
        .filter(({ poiEnabled }) => poiEnabled)
        .debounceTime(350)
        .combineLatest(
            isAnimating,
            hitmapUpdates.map(() => Scheduler.now()),
            curPoints.map(({ buffer, byteOffset, byteLength }) =>
                new Float32Array(buffer, byteOffset, byteLength / Float32Array.BYTES_PER_ELEMENT)
            ),
            (props, animating, hitMapUpdateTime, points) => ({
                ...props,
                poiMaxAsCount: props.poiMax,
                hitMapUpdateTime,
                canResample: !animating && props.poiEnabled,
                points
            })
        )
        // .filter(({ poiEnabled }) => poiEnabled)
        .distinctUntilChanged(
            (prev, { zoom, canResample, hitMapUpdateTime, poiMax }) =>
                zoom === prev.zoom &&
                hitMapUpdateTime === prev.hitMapUpdateTime &&
                // (vboUpdate === prev.vboUpdate || vboUpdate !== 'received') &&
                (poiMax === prev.poiMax) && 
                (canResample === prev.canResample || canResample === false)
        )
        .scan(scanResampleLabelHits, {})
        .distinctUntilChanged(
            ({ hits, poiMax: poiMaxPrev }, { hits: newHits, didResample, poiMax }) =>
                (poiMaxPrev === poiMax) && 
                !didResample && (!newHits || shallowEqual(hits, newHits))
        )
        .map(setLabelHitReferences);
}

function scanResampleLabelHits(memo, props) {
    let didResample = false;
    const t = Scheduler.now();
    const { falcor, renderState, canResample, poiMaxAsCount, poiMax, points } = props;
    const { camera, canvas } = renderState || {};

    let { hits, lastResampleTime = Scheduler.now() } = memo;

    if (canResample && (!hits || t - lastResampleTime > TIME_BETWEEN_SAMPLES)) {
        const totalNodes = renderState
            && renderState.numElements
            && (renderState.numElements.point || renderState.numElements.pointculled);

        if (totalNodes && poiMaxAsCount >= totalNodes) {
            didResample = true;
            lastResampleTime = t;

            // Calculate canvas/viewport bounds in world coordinates (x0, x1, y0, y1)
            let x0 = 0, x1 = 0, y0 = 0, y1 = 0;
            if (camera && canvas) {
                const topLeft = camera.canvas2WorldCoords(0, 0, canvas);
                const bottomRight = camera.canvas2WorldCoords(canvas.width / camera.pixelRatio, canvas.height / camera.pixelRatio, canvas);
                x0 = topLeft.x;
                x1 = bottomRight.x;
                y0 = bottomRight.y;
                y1 = topLeft.y;
            }

            hits = [];
            if (points && camera) {
                for (let i = 0; i < points.length; i += 2) {
                    const nodeX = points[i];
                    const nodeY = points[i + 1];
                    if (nodeX >= x0 && nodeX <= x1 && nodeY >= y0 && nodeY <= y1) {
                        hits.push(String(i / 2));
                    }
                }
            }
        } else {
            const { pixelreads = {} } = renderState;
            const { pointHitmapDownsampled } = pixelreads;
            if (pointHitmapDownsampled) {
                // console.log(`time since last resample ${(t - lastResampleTime)|0}ms`);
                didResample = true;
                lastResampleTime = t;
                hits = sortHits(markHits(pointHitmapDownsampled), poiMaxAsCount);
                // console.log(`hit resample time ${(Scheduler.now() - t)|0}ms`);
                // console.log(``);
                // console.log(`resampled label hits: ${JSON.stringify(hits)}`);
            }
        }
    }

    return { ...memo, hits, falcor, didResample, lastResampleTime, poiMaxAsCount, poiMax };
}

function setLabelHitReferences({ falcor, hits, poiMaxAsCount }) {
    let index = 0;
    let { length } = hits || [];
    length = Math.min(length, poiMaxAsCount);

    const vals = { point: { length } };
    const path = falcor.getPath().slice(0, 4);
    const invs = (length < poiMaxAsCount && { point: {} }) || undefined;

    while (index < length) {
        vals.point[index] = $ref(path.concat('labelsByType', 'point', hits[index++]));
    }

    while (index < poiMaxAsCount) {
        invs.point[index++] = true;
    }

    return {
        falcor,
        values: [{ json: vals }],
        invalidations: (invs && [{ json: invs }]) || []
    };
}

function sortHits(hits, poiMaxAsCount) {
    return _.keys(hits)
        .sort((a, b) => hits[b] - hits[a])
        .slice(0, poiMaxAsCount);
}

function markHits({ buffer, byteOffset, byteLength }) {
    // Approach two (straight count + incr)
    // O(N), but slams memory

    // for (let i = 0; i < samples32.length; i++) {
    //     idx = decodeGpuIndex(samples32[i]);
    //     hits[idx] = hits[idx] ? hits[idx] + 1 : 1;
    // }

    // Approach one (sort -> count)
    // O(NlogN), but less slamming memory
    const hits = Object.create(null);
    const samples = new Uint32Array(
            buffer,
            byteOffset,
            byteLength / Uint32Array.BYTES_PER_ELEMENT
    ).sort((a, b) => a - b);

    let left = -1;
    let score = 0;
    let right = -1;
    let count = -1;
    let { length } = samples;

    while (++count < length) {
        // Exclude misses (-1)
        // (conditions are faster than hash sets)
        if (~(right = decodeGpuIndex(samples[count]))) {
            if (right === left) {
                score++;
            } else if (~left === 0) {
                score = 1;
                left = right;
            } else {
                hits[left] = score;
                left = right;
                score = 1;
            }
        }
    }

    if (~left) {
        hits[left] = score;
    }

    return hits;
}
